import React, { FC, useEffect, useRef, useState } from 'react';

interface MindARMarkerDetectorProps {
  mindFileSrc: string;
  onMarkerFound: (targetIndex: number) => void;
  onARReady: () => void;
  assets: Array<any>;
  showMainMenu?: boolean;
}

const MindARMarkerDetector: FC<MindARMarkerDetectorProps> = ({
  mindFileSrc,
  onMarkerFound,
  onARReady,
  assets,
  showMainMenu,
}) => {
  const sceneRef = useRef<any>(null);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(
    window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
  );

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = async () => {
      const newOrientation = window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
      if (newOrientation !== orientation) {
        setOrientation(newOrientation);
        
        // Get the AR system and restart it
        const sceneEl = sceneRef.current;
        if (sceneEl) {
          const arSystem = sceneEl.systems['mindar-image-system'];
          if (arSystem) {
            try {
              // First check if AR is running
              if (arSystem.isRunning) {
                await arSystem.stop();
              }
              
              // Wait a bit for the camera to adjust
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Start a new AR session
              await arSystem.start();
              console.log('AR system restarted after orientation change');
            } catch (error) {
              console.error("Error during AR system restart:", error);
            }
          }
        }
      }
    };

    let resizeTimeout: number | null = null;
    
    // Debounce the resize handler
    const debouncedOrientationChange = () => {
      if (resizeTimeout) {
        window.clearTimeout(resizeTimeout);
      }
      resizeTimeout = window.setTimeout(handleOrientationChange, 150);
    };

    // Add resize listener for orientation changes
    window.addEventListener('resize', debouncedOrientationChange);
    return () => {
      window.removeEventListener('resize', debouncedOrientationChange);
      if (resizeTimeout) {
        window.clearTimeout(resizeTimeout);
      }
    };
  }, [orientation]);

  useEffect(() => {
    const sceneEl = sceneRef.current;

    if (!sceneEl) {
      console.error('Scene ref is null');
      return;
    }

    const handleSceneLoaded = () => {
      const arSystem = sceneEl.systems['mindar-image-system'];
      if (arSystem) {
        const startResult = arSystem.start();
        if (startResult && typeof startResult.then === 'function') {
          startResult.then(() => {
            onARReady();
          }).catch((error: any) => {
            console.error("Failed to start AR system:", error);
          });
        } else {
          onARReady();
        }
      }
    };

    const handleMarkerFound = (event: any) => {
      const targetIndex = event.target.getAttribute('mindar-image-target').targetIndex;
      console.log(`Marker found with index: ${targetIndex}`);
      onMarkerFound(parseInt(targetIndex, 10));
    };

    sceneEl.addEventListener('loaded', handleSceneLoaded);
    sceneEl.addEventListener('targetFound', handleMarkerFound);

    return () => {
      sceneEl.removeEventListener('loaded', handleSceneLoaded);
      sceneEl.removeEventListener('targetFound', handleMarkerFound);
    };
  }, [mindFileSrc, onMarkerFound, onARReady]);

  return (
    <a-scene
      ref={sceneRef}
      mindar-image={`imageTargetSrc: ${mindFileSrc}; autoStart: true; uiScanning: no; uiLoading: no;`}
      style={{ 
        display: showMainMenu ? "none" : "block",
        position: 'absolute', 
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%', 
        height: '100%',
        minWidth: '100%',
        minHeight: '100%',
        overflow: 'hidden'
       }}
    >
      <a-camera position="0 0 0" look-controls="enabled: false"></a-camera>

      {assets.map((asset, index) => (
        <a-entity
          key={`target-${index}`}
          mindar-image-target={`targetIndex: ${index}`}
          visible="false"
        >
          <a-plane
            color="blue"
            opacity="0.5"
            position="0 0 0"
            height="0.552"
            width="1"
            rotation="0 0 0"
          ></a-plane>
        </a-entity>
      ))}
    </a-scene>
  );
};

export default MindARMarkerDetector;